import React, { useMemo } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import {
  buildImportMappingPreviewMatrix,
  importMappingTypeLabel,
} from '../../utils/importMappingPreview'

/**
 * Read-only pipe column × template comparison modal.
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   mappingType: string,
 *   templates: Array<{ id?: string|number, template_name?: string, mapping_type?: string, template_content?: object }>,
 *   currentContent?: object|null,
 * }} props
 */
export default function ImportMappingPreviewModal({
  open,
  onClose,
  mappingType,
  templates = [],
  currentContent = null,
}) {
  const matrix = useMemo(() => {
    if (!open) return { columns: [], maxCol: -1, rows: [] }

    const cols = []
    const typeTemplates = (templates || []).filter(
      (t) => String(t.mapping_type || '') === String(mappingType || '')
    )

    for (const t of typeTemplates) {
      const content = t.template_content
      const fmt = String(content?.sourceFormat || '').toLowerCase()
      if (fmt !== 'pipe-delimited' && fmt !== 'pipe') continue
      cols.push({
        id: String(t.id ?? t.template_name),
        name: t.template_name || `Template ${t.id}`,
        content,
      })
    }

    const curFmt = String(currentContent?.sourceFormat || '').toLowerCase()
    if (currentContent && (curFmt === 'pipe-delimited' || curFmt === 'pipe')) {
      cols.push({
        id: '__current__',
        name: 'Current (unsaved)',
        content: currentContent,
      })
    }

    return buildImportMappingPreviewMatrix(cols)
  }, [open, mappingType, templates, currentContent])

  if (!open) return null

  const typeLabel = importMappingTypeLabel(mappingType)
  const excelSkipped = (templates || []).filter(
    (t) =>
      String(t.mapping_type || '') === String(mappingType || '') &&
      String(t.template_content?.sourceFormat || 'excel').toLowerCase() !== 'pipe-delimited' &&
      String(t.template_content?.sourceFormat || '').toLowerCase() !== 'pipe'
  ).length

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-mapping-preview-title"
    >
      <div className="flex min-h-full items-start justify-center p-4 sm:p-6">
        <button
          type="button"
          className="fixed inset-0 bg-gray-600/50"
          aria-label="Close preview"
          onClick={onClose}
        />
        <div className="relative z-10 w-full max-w-6xl rounded-lg bg-white shadow-xl">
          <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
            <div>
              <h2
                id="import-mapping-preview-title"
                className="text-lg font-semibold text-gray-900"
              >
                Column mapping preview — {typeLabel}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Rows are pipe column indexes. Columns are templates. Empty cells mean that template
                does not use that column.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="px-5 py-4">
            {matrix.columns.length === 0 ? (
              <p className="text-sm text-gray-600">
                No pipe-delimited templates for this import type.
                {excelSkipped > 0
                  ? ` ${excelSkipped} Excel/header template(s) are hidden in this view.`
                  : ' Load or create a pipe-delimited template first.'}
              </p>
            ) : (
              <>
                {excelSkipped > 0 && (
                  <p className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
                    Excel templates use header names — not shown here ({excelSkipped} skipped).
                  </p>
                )}
                <div className="overflow-auto max-h-[70vh] border border-gray-200 rounded-lg">
                  <table className="min-w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="sticky left-0 z-20 bg-gray-50 px-3 py-2 text-left font-medium text-gray-700 border-b border-r border-gray-200 whitespace-nowrap">
                          Col #
                        </th>
                        {matrix.columns.map((c) => (
                          <th
                            key={c.id}
                            className={`px-3 py-2 text-left font-medium text-gray-800 border-b border-gray-200 min-w-[10rem] ${
                              c.id === '__current__' ? 'bg-blue-50' : ''
                            }`}
                          >
                            {c.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {matrix.rows.map((row) => (
                        <tr key={row} className="hover:bg-gray-50/80">
                          <td className="sticky left-0 z-10 bg-white px-3 py-2 font-mono text-xs text-gray-700 border-b border-r border-gray-100 whitespace-nowrap">
                            Col {row}
                          </td>
                          {matrix.columns.map((c) => {
                            const cell = c.byCol[row]
                            const fields = cell?.fields || []
                            const label = cell?.label
                            return (
                              <td
                                key={`${c.id}-${row}`}
                                className={`px-3 py-2 align-top border-b border-gray-100 ${
                                  c.id === '__current__' ? 'bg-blue-50/40' : ''
                                }`}
                              >
                                {fields.length === 0 && !label ? (
                                  <span className="text-gray-400">—</span>
                                ) : (
                                  <div>
                                    {fields.length > 0 ? (
                                      <div className="font-mono text-xs text-gray-900 space-y-0.5">
                                        {fields.map((f) => (
                                          <div key={f}>{f}</div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">—</span>
                                    )}
                                    {label ? (
                                      <div className="text-[11px] text-gray-500 mt-0.5 font-sans">
                                        {label}
                                      </div>
                                    ) : null}
                                  </div>
                                )}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end border-t border-gray-200 px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-md text-sm bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
